import type {
  AccessGroupSize,
  AccessRequestInfo,
  AccessUse,
  CostReport,
  CheckClipInfo,
  CheckVerdict,
  ClientConfig,
  CreateStudioResponse,
  FeedbackItem,
  FeedbackList,
  FeedbackPriority,
  FeedbackStatus,
  FeedbackSubmission,
  RegisterTrackRequest,
  RegisterTrackResponse,
  AccountInfo,
  InviteInfo,
  SignInPending,
  SignInResponse,
  StudioInfo,
  StudioSummary,
  TakeInfo,
  TrackFinishRequest,
  TrackInfo,
  StudioSettings,
} from '../../../shared/protocol.ts';

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(body?.error ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

export const SESSION_KEY = 'takehouse.session';
/** This browser's sign-in, sent with every API call so the server knows who is asking. */
function sessionHeader(): Record<string, string> {
  try {
    const t = localStorage.getItem(SESSION_KEY);
    return t ? { 'x-account': t } : {};
  } catch {
    return {};
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { ...init, headers: { ...sessionHeader(), ...(init.headers as Record<string, string> | undefined) } });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

const jsonInit = (method: string, body: unknown, headers: Record<string, string> = {}): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json', ...headers },
  body: JSON.stringify(body),
});

export const api = {
  /** With a studio link, includes relay (TURN) logins when the server has a relay. */
  config: (studioId?: string, key?: string) =>
    call<ClientConfig>(studioId && key ? `/api/config?studio=${encodeURIComponent(studioId)}` : '/api/config', key ? { headers: { 'x-studio-key': key } } : {}),
  signIn: (email: string, name?: string) => call<SignInResponse | SignInPending>('/api/session', jsonInit('POST', { email, name })),
  verifySignIn: (email: string, code: string) => call<SignInResponse>('/api/session/verify', jsonInit('POST', { email, code })),
  signOut: () => call<{ ok: true }>('/api/session', { method: 'DELETE' }),
  me: () => call<AccountInfo>('/api/me'),
  rename: (name: string) => call<AccountInfo>('/api/me', jsonInit('PATCH', { name })),
  myStudios: () => call<StudioSummary[]>('/api/me/studios'),
  costs: () => call<CostReport>('/api/admin/costs'),
  requestAccess: (req: { use: AccessUse; groupSize: AccessGroupSize; note?: string }) => call<AccountInfo>('/api/me/access-request', jsonInit('POST', req)),
  sendFeedback: (f: FeedbackSubmission) => call<{ id: string }>('/api/feedback', jsonInit('POST', f)),
  feedback: (status?: FeedbackStatus | null) => call<FeedbackList>(status ? `/api/admin/feedback?status=${status}` : '/api/admin/feedback'),
  feedbackItem: (id: string) => call<FeedbackItem>(`/api/admin/feedback/${id}`),
  updateFeedback: (id: string, patch: { status?: FeedbackStatus; priority?: FeedbackPriority | null }) =>
    call<FeedbackItem>(`/api/admin/feedback/${id}`, jsonInit('PATCH', patch)),
  commentOnFeedback: (id: string, body: string) => call<FeedbackItem>(`/api/admin/feedback/${id}/comments`, jsonInit('POST', { body })),
  deleteFeedback: (id: string) => call<{ ok: true }>(`/api/admin/feedback/${id}`, { method: 'DELETE' }),
  accessRequests: () => call<AccessRequestInfo[]>('/api/admin/access-requests'),
  decideAccess: (email: string, decision: 'approve' | 'decline') =>
    call<AccessRequestInfo>(`/api/admin/access-requests/${encodeURIComponent(email)}`, jsonInit('POST', { decision })),
  signOutEverywhere: () => call<{ ok: true }>('/api/me/sessions', { method: 'DELETE' }),
  deleteStudio: (id: string, key: string) =>
    call<{ takes: number; files: number; bytes: number }>(`/api/studios/${id}`, { method: 'DELETE', headers: { 'x-studio-key': key } }),
  deleteTake: (takeId: string, key: string) =>
    call<{ files: number; bytes: number }>(`/api/takes/${takeId}`, { method: 'DELETE', headers: { 'x-studio-key': key } }),
  invites: (id: string, key: string) => call<InviteInfo[]>(`/api/studios/${id}/invites`, { headers: { 'x-studio-key': key } }),
  invite: (id: string, key: string, email: string, role: 'producer' | 'guest') =>
    call<{ email: string; role: string; emailed: boolean }>(`/api/studios/${id}/invites`, jsonInit('POST', { email, role }, { 'x-studio-key': key })),
  removeInvite: (id: string, key: string, email: string) =>
    call<{ ok: true }>(`/api/studios/${id}/invites/${encodeURIComponent(email)}`, { method: 'DELETE', headers: { 'x-studio-key': key } }),
  createStudio: (name: string) => call<CreateStudioResponse>('/api/studios', jsonInit('POST', { name })),
  studio: (id: string, key: string) => call<StudioInfo>(`/api/studios/${id}`, { headers: { 'x-studio-key': key } }),
  takes: (id: string, key: string) => call<TakeInfo[]>(`/api/studios/${id}/takes`, { headers: { 'x-studio-key': key } }),
  studioSettings: (id: string, key: string) => call<StudioSettings>(`/api/studios/${id}/settings`, { headers: { 'x-studio-key': key } }),
  updateStudioSettings: (id: string, key: string, patch: Partial<StudioSettings>) =>
    call<StudioSettings>(`/api/studios/${id}/settings`, jsonInit('PATCH', patch, { 'x-studio-key': key })),
  keepTake: (takeId: string, key: string) => call<TakeInfo>(`/api/takes/${takeId}/keep`, { method: 'POST', headers: { 'x-studio-key': key } }),
  setRetention: (id: string, key: string, days: number | null) =>
    call<{ retentionDays: number | null }>(`/api/studios/${id}/retention`, jsonInit('PUT', { days }, { 'x-studio-key': key })),
  transcribe: (takeId: string, key: string) =>
    call<TakeInfo['transcript']>(`/api/takes/${takeId}/transcribe`, { method: 'POST', headers: { 'x-studio-key': key } }),
  registerTrack: (key: string, req: RegisterTrackRequest) =>
    call<RegisterTrackResponse>('/api/tracks', jsonInit('POST', req, { 'x-studio-key': key })),
  uploadStatus: (trackId: string, token: string) =>
    call<{ received: number; status: string }>(`/api/tracks/${trackId}/upload`, { headers: { 'x-upload-token': token } }),
  finishTrack: (trackId: string, token: string, req: TrackFinishRequest) =>
    call<TrackInfo>(`/api/tracks/${trackId}/finish`, jsonInit('POST', req, { 'x-upload-token': token })),
  uploadJournal: (takeId: string, key: string, participantId: string, journal: unknown) =>
    call<{ ok: true }>(`/api/takes/${takeId}/journal`, jsonInit('POST', { participantId, journal }, { 'x-studio-key': key })),
    uploadCheckClip: (
    checkId: string,
    key: string,
    meta: { participantId: string; participantName: string; label: string; kind: string; mime: string },
    verdict: CheckVerdict,
    body: Blob,
  ) =>
    call<CheckClipInfo>(`/api/checks/${checkId}/clips?${new URLSearchParams(meta)}`, {
      method: 'POST',
      headers: { 'x-studio-key': key, 'x-verdict': encodeURIComponent(JSON.stringify(verdict)) },
      body,
    }),
};

export const fileUrl = (trackId: string, key: string, download = false) =>
  `/api/tracks/${trackId}/file?k=${encodeURIComponent(key)}${download ? '&download=1' : ''}`;
export const checkClipUrl = (clipId: string, key: string) =>
  `/api/check-clips/${clipId}/file?k=${encodeURIComponent(key)}`;
export const takeUrl = (takeId: string, key: string, file: string) =>
  `/api/takes/${takeId}/${file}?k=${encodeURIComponent(key)}`;
export const transcriptUrl = (takeId: string, key: string, file: string) =>
  `/api/takes/${takeId}/transcript/${file}?k=${encodeURIComponent(key)}`;
